import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Loader2, ChevronLeft, CheckCircle2, ShieldCheck } from "lucide-react";
import { DiscoveryQuestion } from "../../components/discovery/DiscoveryQuestion";
import { ReactionCard } from "../../components/discovery/ReactionCard";
import { usePageMeta } from "../../lib/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Indice di manualità 0–100 (formula v1, documentata) ──
// base = ore ripetitive (b_003): "Meno di 5"→15, "5–10"→40, "10–20"→70, "Più di 20"→95
// +10 se i solleciti sono creati uno a uno (cr_002) o i morosi cercati a mano (cr_001)
// +5 se tra gli strumenti (b_001) ci sono sia Excel sia Carta
// clamp 0–100 · fasce: ≥70 ALTO · 40–69 MEDIO · <40 BASSO
const HOURS_SCORE = { "Meno di 5": 15, "5–10": 40, "10–20": 70, "Più di 20": 95 };
export const computeMiniResult = (answers) => {
  let score = HOURS_SCORE[answers.b_003] ?? 40;
  if (["Da modello, ma uno a uno", "Scritti a mano uno a uno"].includes(answers.cr_002)) score += 10;
  if (["Estrazione manuale / Excel", "Controllo a mano cliente per cliente"].includes(answers.cr_001)) score += 10;
  const tools = answers.b_001 || [];
  if (tools.includes("Excel / fogli di calcolo") && tools.includes("Carta e documenti fisici")) score += 5;
  score = Math.max(0, Math.min(100, score));
  const band = score >= 70 ? "ALTO" : score >= 40 ? "MEDIO" : "BASSO";
  const note = {
    ALTO: "sei nella fascia che perde più ore in attività ripetitive.",
    MEDIO: "c'è margine concreto per recuperare ore ogni settimana.",
    BASSO: "buona base — l'automazione può comunque togliere gli ultimi attriti.",
  }[band];
  return { score, band, note };
};

const STEPS = ["profilo", "strumenti", "area", "priorita", "finale"];
const STEP_TITLES = {
  profilo: "Chi sei",
  strumenti: "Strumenti e abitudini",
  area: "La tua area",
  priorita: "Pain e priorità",
  finale: "Il tuo risultato",
};

const draftKey = (slug) => `wl_discovery_draft_${slug}`;
const isMobile = () => window.matchMedia("(max-width: 768px), (pointer: coarse)").matches;

export default function DiscoveryPublic() {
  usePageMeta({ title: "Wattline", noindex: true });
  const { slug } = useParams();
  const [params] = useSearchParams();
  const [phase, setPhase] = useState("loading"); // loading | error | landing | form | done
  const [errorMsg, setErrorMsg] = useState("");
  const [meta, setMeta] = useState(null);
  const [hadDraft, setHadDraft] = useState(false);

  const [step, setStep] = useState(0);
  const [area, setArea] = useState("");
  const [respondent, setRespondent] = useState({ name: "", role: "" });
  const [answers, setAnswers] = useState({});
  const [contact, setContact] = useState({ name: "", company: "", email: "", phone: "", marketing_consent: false });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [contactLeft, setContactLeft] = useState(false);

  const startedAtRef = useRef(new Date().toISOString());
  const shownReactionsRef = useRef(new Set());
  const [activeReaction, setActiveReaction] = useState(null);

  useEffect(() => {
    axios.get(`${API}/d/${slug}`)
      .then(({ data }) => {
        setMeta(data);
        const raw = localStorage.getItem(draftKey(slug));
        if (raw) {
          try {
            const d = JSON.parse(raw);
            if (d.answers && Object.keys(d.answers).length) setHadDraft(true);
          } catch { /* ignora */ }
        }
        setPhase("landing");
      })
      .catch((err) => {
        setErrorMsg(err?.response?.data?.detail || "Questionario non disponibile.");
        setPhase("error");
      });
  }, [slug]);

  // bozza in localStorage a ogni cambiamento
  useEffect(() => {
    if (phase !== "form") return;
    localStorage.setItem(draftKey(slug), JSON.stringify({
      step, area, respondent, answers, savedAt: new Date().toISOString(),
    }));
  }, [phase, step, area, respondent, answers, slug]);

  const resumeDraft = () => {
    try {
      const d = JSON.parse(localStorage.getItem(draftKey(slug)));
      setStep(d.step || 0);
      setArea(d.area || "");
      setRespondent(d.respondent || { name: "", role: "" });
      setAnswers(d.answers || {});
    } catch { /* riparte pulito */ }
    setPhase("form");
  };
  const startFresh = () => {
    localStorage.removeItem(draftKey(slug));
    setStep(0); setArea(""); setAnswers({});
    setPhase("form");
  };

  const bank = useMemo(() => meta?.questions || [], [meta]);
  const byArea = useMemo(() => {
    const g = {};
    bank.forEach((q) => { (g[q.area] = g[q.area] || []).push(q); });
    return g;
  }, [bank]);

  const stepQuestions = useMemo(() => {
    const name = STEPS[step];
    if (name === "strumenti") return byArea.common_b || [];
    if (name === "area") return byArea[area] || [];
    if (name === "priorita") return byArea.common_d || [];
    return [];
  }, [step, byArea, area]);

  const maybeReact = useCallback((q, value) => {
    if (!meta?.settings?.reactions) return;
    if (shownReactionsRef.current.size >= 3) return;
    for (const r of q.reactions || []) {
      const vals = r.when?.values || [];
      const hit = Array.isArray(value) ? value.some((v) => vals.includes(v)) : vals.includes(value);
      if (hit && !shownReactionsRef.current.has(q.qid)) {
        shownReactionsRef.current.add(q.qid);
        setActiveReaction({ text: r.text });
        return;
      }
    }
  }, [meta]);

  const setAnswer = (q, value) => {
    setAnswers((prev) => ({ ...prev, [q.qid]: value }));
    maybeReact(q, value);
  };

  const stepValid = () => {
    const name = STEPS[step];
    if (name === "profilo") return !!area;
    if (name === "finale") return true;
    return stepQuestions.every((q) => {
      if (!q.required) return true;
      const v = answers[q.qid];
      if (Array.isArray(v)) return v.length > 0;
      return v !== undefined && v !== null && String(v).trim() !== "";
    });
  };

  const contactMode = meta?.settings?.contact || "required";
  const consentOk = contact.marketing_consent && (contact.email || "").trim();
  // Contatti sempre facoltativi: si inviano solo se l'utente apre il blocco e acconsente
  const canSubmit = contactMode === "off" ? true : (!contactLeft || consentOk);

  const submit = async () => {
    setSubmitting(true);
    setSubmitError("");
    const visibleAreas = new Set(["common_b", area, "common_d"]);
    const payloadAnswers = bank
      .filter((q) => visibleAreas.has(q.area))
      .map((q) => ({ qid: q.qid, version: q.version, value: answers[q.qid] }))
      .filter((a) => a.value !== undefined && a.value !== null &&
        (Array.isArray(a.value) ? a.value.length > 0 : String(a.value).trim() !== ""));
    const leaveContact = contactMode !== "off" && contactLeft && consentOk;
    const source = {};
    for (const [k, v] of params.entries()) if (k.startsWith("utm_")) source[k] = v;
    try {
      await axios.post(`${API}/d/${slug}/responses`, {
        area,
        respondent: { name: respondent.name.trim() || null, role: respondent.role.trim() || null },
        contact: leaveContact ? contact : null,
        answers: payloadAnswers,
        meta: {
          started_at: startedAtRef.current,
          duration_s: Math.round((Date.now() - new Date(startedAtRef.current).getTime()) / 1000),
          user_agent_class: isMobile() ? "mobile" : "desktop",
        },
        source,
      });
      localStorage.removeItem(draftKey(slug));
      setContactLeft(leaveContact);
      setPhase("done");
      window.scrollTo(0, 0);
    } catch (err) {
      setSubmitError(err?.response?.data?.detail || "Invio non riuscito. Riprova.");
    } finally {
      setSubmitting(false);
    }
  };

  const mini = useMemo(() => computeMiniResult(answers), [answers]);

  // ── RENDER ──
  if (phase === "loading") {
    return (
      <Shell><div className="py-24 text-center text-text-muted"><Loader2 className="animate-spin mx-auto" size={22} /></div></Shell>
    );
  }
  if (phase === "error") {
    return (
      <Shell>
        <div className="py-20 text-center" data-testid="discovery-error">
          <div className="text-lg font-medium text-text-primary mb-2">Questionario non disponibile</div>
          <p className="text-sm text-text-secondary">{errorMsg}</p>
        </div>
      </Shell>
    );
  }
  if (phase === "done") {
    return (
      <Shell>
        <div className="py-16 text-center" data-testid="discovery-done">
          <CheckCircle2 size={40} className="mx-auto text-success-strong mb-4" strokeWidth={1.5} />
          <h1 className="text-2xl font-semibold text-text-primary mb-2">Grazie!</h1>
          <p className="text-sm text-text-secondary max-w-md mx-auto">
            Le tue risposte sono state inviate.
            {contactLeft && (
              <span className="block mt-3 font-medium text-text-primary">
                Ti ricontatto personalmente entro 48 ore. — Mara
              </span>
            )}
          </p>
        </div>
      </Shell>
    );
  }
  if (phase === "landing") {
    return (
      <Shell>
        <div className="py-10 sm:py-16 max-w-xl mx-auto" data-testid="discovery-landing">
          <div className="text-[11px] uppercase tracking-[0.18em] text-text-muted mb-3">Assessment AI</div>
          <h1 className="text-3xl sm:text-4xl font-semibold text-text-primary leading-tight mb-4">{meta.title}</h1>
          <p className="text-[15px] text-text-secondary leading-relaxed mb-6">
            Sei domande sui tuoi strumenti, i tuoi processi e dove perdi tempo.
            <b> 6–8 minuti</b>, anche da telefono, senza registrazione. Alla fine vedi subito
            il tuo indice di manualità.
          </p>
          {hadDraft ? (
            <div className="flex flex-col sm:flex-row gap-3 mb-8">
              <button type="button" onClick={resumeDraft} data-testid="discovery-resume-btn" className="wl-btn wl-btn--primary">
                Bentornato! Riprendi da dove eri
              </button>
              <button type="button" onClick={startFresh} data-testid="discovery-restart-btn" className="wl-btn wl-btn--ghost">
                Ricomincia da capo
              </button>
            </div>
          ) : (
            <button type="button" onClick={startFresh} data-testid="discovery-start-btn" className="wl-btn wl-btn--primary mb-8">
              Inizia il questionario
            </button>
          )}
          <div className="bg-subtle border border-border rounded-lg p-4 flex gap-3" data-testid="discovery-privacy">
            <ShieldCheck size={16} className="text-text-muted shrink-0 mt-0.5" />
            <p className="text-xs text-text-secondary leading-relaxed">{meta.privacy_text}</p>
          </div>
        </div>
      </Shell>
    );
  }

  // FORM
  const stepName = STEPS[step];
  const progress = ((step + 1) / STEPS.length) * 100;

  return (
    <Shell>
      {/* progress bar sempre visibile */}
      <div className="sticky top-0 z-40 -mx-4 sm:mx-0 bg-page/95 backdrop-blur pt-3 pb-2 px-4 sm:px-0">
        <div className="flex items-center justify-between text-xs text-text-muted mb-1.5">
          <span data-testid="discovery-step-title" className="font-medium text-text-secondary">
            {STEP_TITLES[stepName]}
          </span>
          <span>{step + 1} / {STEPS.length}</span>
        </div>
        <div className="h-1.5 bg-subtle rounded-full overflow-hidden">
          <div className="h-full bg-accent rounded-full transition-all" style={{ width: `${progress}%` }} data-testid="discovery-progress" />
        </div>
      </div>

      <div className="max-w-xl mx-auto py-6" data-testid={`discovery-step-${stepName}`}>
        {stepName === "profilo" && (
          <>
            <div className="wl-field mb-5">
              <label className="wl-label" htmlFor="disc-name">Come ti chiami? <span className="text-text-muted font-normal">(facoltativo)</span></label>
              <input id="disc-name" value={respondent.name} maxLength={100}
                     onChange={(e) => setRespondent({ ...respondent, name: e.target.value })}
                     className="wl-input" data-testid="discovery-name-input" />
            </div>
            <div className="wl-field mb-5">
              <label className="wl-label" htmlFor="disc-role">Qual è il tuo ruolo?</label>
              <input id="disc-role" value={respondent.role} maxLength={100} placeholder="es. Impiegata backoffice, Titolare…"
                     onChange={(e) => setRespondent({ ...respondent, role: e.target.value })}
                     className="wl-input" data-testid="discovery-role-input" />
            </div>
            <div className="mb-2 text-[15px] font-medium text-text-primary">In quale area lavori?</div>
            <div className="flex flex-col gap-2" role="radiogroup" aria-label="Area">
              {meta.areas.map((a) => (
                <button key={a.id} type="button" role="radio" aria-checked={area === a.id}
                        onClick={() => setArea(a.id)}
                        data-testid={`discovery-area-${a.id}`}
                        className={`text-left px-4 py-3 rounded-lg border text-sm transition-colors ${
                          area === a.id
                            ? "border-accent bg-accent-subtle text-accent-strong font-medium"
                            : "border-border bg-surface text-text-primary hover:border-accent/50"
                        }`}>
                  {a.label}
                </button>
              ))}
            </div>
          </>
        )}

        {["strumenti", "area", "priorita"].includes(stepName) &&
          stepQuestions.map((q) => (
            <DiscoveryQuestion key={q.qid} q={q} value={answers[q.qid]} onChange={(v) => setAnswer(q, v)} />
          ))}

        {stepName === "finale" && (
          <>
            <div className="bg-surface border border-border rounded-xl p-6 mb-6 text-center" data-testid="discovery-mini-result">
              <div className="text-[11px] uppercase tracking-[0.18em] text-text-muted mb-2">Indice di manualità</div>
              <div className={`text-4xl font-semibold mb-1 ${mini.band === "ALTO" ? "text-danger" : mini.band === "MEDIO" ? "text-warning-strong" : "text-success-strong"}`}>
                {mini.band}
              </div>
              <div className="text-xs text-text-muted mb-2">{mini.score} / 100</div>
              <p className="text-sm text-text-secondary">{mini.note}</p>
            </div>

            {contactMode !== "off" && (
              <div data-testid="discovery-contact-block">
                <div className="text-[15px] font-medium text-text-primary mb-1">
                  Vuoi essere ricontattato? <span className="text-text-muted font-normal">(facoltativo)</span>
                </div>
                <label className="flex items-center gap-2 text-sm text-text-secondary my-3">
                  <input type="checkbox" checked={contactLeft} onChange={(e) => setContactLeft(e.target.checked)}
                         data-testid="discovery-contact-toggle" className="w-4 h-4" />
                  Sì, lascio i miei contatti
                </label>
                {contactLeft && (
                  <div className="space-y-3 mt-3">
                    <input value={contact.name} maxLength={100} placeholder="Nome e cognome (facoltativo)"
                           onChange={(e) => setContact({ ...contact, name: e.target.value })}
                           className="wl-input w-full" data-testid="discovery-contact-name" />
                    <input value={contact.company} maxLength={120} placeholder="Azienda (facoltativo)"
                           onChange={(e) => setContact({ ...contact, company: e.target.value })}
                           className="wl-input w-full" data-testid="discovery-contact-company" />
                    <input value={contact.email} maxLength={200} type="email" placeholder="Email (necessaria solo per il ricontatto)"
                           onChange={(e) => setContact({ ...contact, email: e.target.value })}
                           className="wl-input w-full" data-testid="discovery-contact-email" />
                    <input value={contact.phone} maxLength={30} placeholder="Telefono (facoltativo)"
                           onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                           className="wl-input w-full" data-testid="discovery-contact-phone" />
                    <label className="flex items-start gap-2.5 text-xs text-text-secondary leading-relaxed">
                      <input type="checkbox" checked={contact.marketing_consent}
                             onChange={(e) => setContact({ ...contact, marketing_consent: e.target.checked })}
                             data-testid="discovery-consent-checkbox" className="w-4 h-4 mt-0.5 shrink-0" />
                      <span>
                        Acconsento a essere ricontattato/a per una presentazione di Wattline (facoltativo)
                      </span>
                    </label>
                  </div>
                )}
              </div>
            )}
            {submitError && (
              <div className="mt-4 text-sm text-danger bg-danger-subtle border border-danger-subtle rounded-lg px-3 py-2" data-testid="discovery-submit-error">
                {submitError}
              </div>
            )}
          </>
        )}

        {/* nav */}
        <div className="flex items-center justify-between mt-8 gap-3">
          {step > 0 ? (
            <button type="button" onClick={() => { setStep(step - 1); window.scrollTo(0, 0); }}
                    data-testid="discovery-back-btn" className="wl-btn wl-btn--ghost">
              <ChevronLeft size={15} className="mr-1" /> Indietro
            </button>
          ) : <span />}
          {stepName !== "finale" ? (
            <button type="button" disabled={!stepValid()}
                    onClick={() => { setStep(step + 1); window.scrollTo(0, 0); }}
                    data-testid="discovery-next-btn" className="wl-btn wl-btn--primary">
              Avanti
            </button>
          ) : (
            <button type="button" disabled={submitting || !canSubmit} onClick={submit}
                    data-testid="discovery-submit-btn" className="wl-btn wl-btn--primary">
              {submitting ? "Invio…" : "Invia le risposte"}
            </button>
          )}
        </div>
      </div>

      <ReactionCard reaction={activeReaction} onDismiss={() => setActiveReaction(null)} />
    </Shell>
  );
}

const Shell = ({ children }) => (
  <div className="min-h-screen bg-page flex flex-col">
    <main className="flex-1 w-full max-w-2xl mx-auto px-4">{children}</main>
    <footer className="py-6 text-center text-xs text-text-muted" data-testid="discovery-footer">
      Powered by <span className="font-semibold text-text-secondary">Wattline</span>
    </footer>
  </div>
);
